import type { GrantProvider } from '../port.js';
import type { AppResourceHint } from './app-resource.js';

export const refusals = [
  'not_allowed',
  'app_not_installed',
  'session_invalid',
  'rate_limited',
  'busy',
  'duplicate',
  'unavailable',
] as const;
export type Refusal = (typeof refusals)[number];
export type HostState =
  | 'idle'
  | 'waiting_init'
  | 'ready'
  | 'timeout'
  | 'renew_needs_gesture'
  | `refused:${Refusal}`;
/**
 * Wire v1.2: who the host says the viewer is. NOT an identity in the authority sense (bridge-adapter's `identity()` is the session with
 * `canWrite`): this is the label the header shows, hence `viewerLabel` on the provider. The pass names no one, so the host MAY send its own word for the person in the INIT
 * (and only there). Display data, never authority: it labels the header, it grants nothing.
 */
export interface ViewerLabel {
  displayName?: string;
  avatarUrl?: string;
}
export type HostColorScheme = 'light' | 'dark';
export function readHostColorScheme(value: unknown): HostColorScheme | undefined {
  return value === 'light' || value === 'dark' ? value : undefined;
}

export interface HostProvider {
  /** Wire v1.4: presentation only; absent leaves the app’s existing theme unchanged. */
  colorScheme?: () => HostColorScheme | undefined;
  grants: GrantProvider;
  /** What the host said about the viewer in its INIT (v1.2); undefined for a v1.1 host or for none. */
  viewerLabel?: () => ViewerLabel | undefined;
  /**
   * CONTRATO-TASKS-MIEMBROS-v1: the host's SIGNED members statement (a JWT string) that travelled with the latest grant (INIT or REFRESH), or undefined.
   * The app only TRANSPORTS it to its own API, which verifies it against the pass: nothing here is trusted, parsed or shown.
   */
  roster?: () => string | undefined;
  getState: () => HostState;
  subscribe: (listener: () => void) => () => void;
  connect?: () => Promise<void>;
  /** EMBED-ROUTE: tell the host where inside the app the person is (embedded hosts only; no-op until the first INIT gave the generation). */
  reportRoute?: (path: string, resource?: AppResourceHint) => void;
}
export function refusal(value: unknown): Refusal {
  return refusals.includes(value as Refusal) ? (value as Refusal) : 'unavailable';
}
export function hostState() {
  let value: HostState = 'idle';
  const listeners = new Set<() => void>();
  return {
    getState: () => value,
    subscribe(this: void, listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set(next: HostState) {
      value = next;
      for (const listener of listeners) listener();
    },
  };
}
export function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
export function trustedOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.username || url.password || url.origin !== value) return null;
    return url.protocol === 'https:' ||
      (url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname))
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

const MAX_NAME = 80;
/** And a ceiling in code points: one grapheme can be an unbounded stack of combining marks (zalgo), which `MAX_NAME` alone would let through. */
const MAX_CODE_POINTS = 320;
const MAX_AVATAR_URL = 2048;
/**
 * What can never be part of how a NAME reads: control characters, lone surrogates (invalid UTF-16 paints as �), line/paragraph separators, the bidi embeddings/overrides/isolates
 * (they spoof how a name reads), and the invisible ones (zero-width space, word joiner, invisible operators, BOM). NOT the joiners
 * U+200C/U+200D nor the direction marks U+200E/U+200F: Persian names need the first, emoji sequences the second, mixed-direction text
 * the third — a blanket ban of the format class (Cf) would drop real names. ALSO the fillers that paint as BLANK (soft hyphen, combining
 * grapheme joiner, the Hangul fillers, the Mongolian vowel separator, the Braille blank, the half-width Hangul filler): a name made of
 * them would show as an empty label.
 */
const UNREADABLE =
  /[\p{Cc}\p{Cs}\p{Zl}\p{Zp}\u200B\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF\u00AD\u115F\u1160\u180E\u2800\u3164\uFFA0]|\u034F/u;
/** At least one character a person can SEE: a letter, a number, a punctuation or a symbol (an emoji is one). Marks alone, or fillers, are not a name. */
const VISIBLE = /[\p{L}\p{N}\p{P}\p{S}]/u;

/** Characters as a person counts them (an emoji with a skin tone is one), not UTF-16 units. */
const graphemes = (text: string): number => [...new Intl.Segmenter().segment(text)].length;

function nameOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const name = value.trim();
  if (name === '' || graphemes(name) > MAX_NAME || Array.from(name).length > MAX_CODE_POINTS)
    return undefined;
  if (UNREADABLE.test(name) || !VISIBLE.test(name)) return undefined;
  return name;
}

function avatarOf(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > MAX_AVATAR_URL) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? value : undefined;
  } catch {
    return undefined;
  }
}

const MAX_ROSTER_CHARS = 64_000;
/** The signed roster the host sent beside a grant, as an opaque string; anything else (absent, not a string, oversize) is `undefined`. */
export function readRoster(payload: Record<string, unknown>): string | undefined {
  const value = payload.roster;
  return typeof value === 'string' && value !== '' && value.length <= MAX_ROSTER_CHARS
    ? value
    : undefined;
}

/** Each field is judged ALONE: a bad avatar never costs the name, and a bad value never costs the INIT its grant. */
export function readViewerLabel(payload: Record<string, unknown>): ViewerLabel | undefined {
  const displayName = nameOf(payload.displayName);
  const avatarUrl = avatarOf(payload.avatarUrl);
  if (displayName === undefined && avatarUrl === undefined) return undefined;
  return {
    ...(displayName === undefined ? {} : { displayName }),
    ...(avatarUrl === undefined ? {} : { avatarUrl }),
  };
}
